import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { useProfilesData } from '@/features/user';
import { MODULE_IDS_TO_SKIP } from '@/lib/utils';

import type {
  ModulesQueryParams,
  RecordableItem,
  RecordingType,
  Session,
  SessionAttendance,
  SessionWithModuleDetails,
  Worksheet,
} from '../types';

import { getModules } from './modules';

// Helper function to map attendance status to boolean
const mapAttendanceStatus = (
  attendance?: SessionAttendance
): boolean | undefined => {
  if (attendance?.status === 'ATTENDED') return true;
  if (attendance?.status === 'MISSED') return false;
  return undefined;
};

// Query Keys for Modules
const modulesKeys = {
  all: ['modules'] as const,
  list: (params?: ModulesQueryParams, variantId?: string) =>
    [...modulesKeys.all, 'list', params, variantId] as const,
} as const;

// Get Modules Query
const useModules = (params: ModulesQueryParams = {}) => {
  const { selectedVariantId } = useProfilesData();

  return useQuery({
    queryKey: modulesKeys.list(params, selectedVariantId),
    queryFn: () => getModules(params),
    // /user/v3/modules reads the selected course from the session and rejects
    // with "Variant ID required" when none is set. Skip the request until
    // profiles has a selected variant so that case is empty, not an error.
    enabled: !!selectedVariantId,
    staleTime: Infinity, // Never stale
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false,
  });
};

// Modules Hook
export const useModulesData = (params: ModulesQueryParams = {}) => {
  const { isLoadingRaw: isProfilesLoading } = useProfilesData();
  const modulesQuery = useModules(params);

  const filteredModules = useMemo(
    () =>
      modulesQuery.data?.modules?.filter(moduleData => {
        for (const id of MODULE_IDS_TO_SKIP) {
          if (moduleData?.module?.id?.includes(id)) {
            return false;
          }
        }
        return true;
      }) ?? [],
    [modulesQuery.data?.modules]
  );

  const allSessions = useMemo(
    () =>
      filteredModules?.flatMap(moduleData =>
        moduleData?.sessions?.map(s => ({
          ...s,
          moduleDetails: moduleData?.module,
        }))
      ) ?? [],
    [filteredModules]
  );

  const sortedAllSessions = useMemo(
    () =>
      [...allSessions].sort(
        (a, b) => (a?.date?.timestamp ?? 0) - (b?.date?.timestamp ?? 0)
      ),
    [allSessions]
  );

  // Given a session ID, get 3 sessions close to it
  const getRelatedSessions = useCallback(
    (sessionId: string): Session[] => {
      const sessionIndex = allSessions.findIndex(s => s.id === sessionId);
      const relatedSessions = allSessions.slice(
        Math.max(0, sessionIndex - 3),
        Math.min(allSessions.length, sessionIndex + 4)
      );
      return relatedSessions.slice(0, 3);
    },
    [allSessions]
  );

  // Generalized function to get related recordings for both sessions and topics
  const getRelatedRecordings = useCallback(
    (recordingId: string, type: RecordingType): RecordableItem[] => {
      if (type === 'session') {
        // Get related sessions
        const sessionIndex = allSessions.findIndex(s => s.id === recordingId);
        const relatedSessions = allSessions.slice(
          Math.max(0, sessionIndex - 3),
          Math.min(allSessions.length, sessionIndex + 4)
        );
        const limitedSessions = relatedSessions.slice(0, 3);

        // Convert to RecordableItem
        return limitedSessions.map(
          (session): RecordableItem => ({
            id: session.id,
            name: session.name,
            videoUrl: session?.video?.url ?? session?.video?.play_url ?? '',
            type: 'session',
            sessionNumber: session.number,
            attendance: mapAttendanceStatus(session.attendance),
            transcriptSessionId: session.id,
            moduleDetails: session.moduleDetails,
          })
        );
      } else {
        // Get related topics from the same module
        const allTopics =
          modulesQuery.data?.on_demand?.flatMap(module =>
            module.topics.map(topic => ({
              ...topic,
              moduleId: module.module_id,
            }))
          ) ?? [];

        // Find the current topic to get its module ID
        const currentTopic = allTopics.find(t => t.id === recordingId);
        if (!currentTopic) return [];

        // Get topics from the same module
        const moduleTopics = allTopics.filter(
          t => t.moduleId === currentTopic.moduleId
        );

        // Get topics around the current one (similar logic to sessions)
        const topicIndex = moduleTopics.findIndex(t => t.id === recordingId);
        const relatedTopics = moduleTopics.slice(
          Math.max(0, topicIndex - 3),
          Math.min(moduleTopics.length, topicIndex + 4)
        );
        const limitedTopics = relatedTopics.slice(0, 3);

        // Convert to RecordableItem
        return limitedTopics.map(
          (topic): RecordableItem => ({
            id: topic.id,
            name: topic.name,
            videoUrl: topic.recording?.manifest_path ?? '',
            type: 'topic',
            moduleNumber: topic.moduleId,
            description: topic.description,
          })
        );
      }
    },
    [allSessions, modulesQuery.data?.on_demand]
  );

  const lastSession: SessionWithModuleDetails | null =
    sortedAllSessions?.at(-1) ?? null;

  const getWorksheetsForModule = useCallback(
    (moduleId: string, questionUrlList: string[] = []): Worksheet[] => {
      const module = modulesQuery.data?.modules?.find(
        m => m.module.id === moduleId
      );
      return (
        module?.worksheets?.filter(
          w =>
            w.question_pdf_s3_url &&
            !questionUrlList?.includes(w.question_pdf_s3_url)
        ) ?? []
      );
    },
    [modulesQuery.data?.modules]
  );

  return {
    // Data
    allModules: modulesQuery.data?.modules ?? [],
    modules: filteredModules,
    lastSession,
    allSessions,
    sortedAllSessions,
    onDemandModules: modulesQuery.data?.on_demand ?? [],
    courseAggregate: modulesQuery.data?.course_aggregate,

    // States
    isLoading:
      isProfilesLoading || modulesQuery.isLoading || modulesQuery.isFetching,
    isError: modulesQuery.isError,
    error: modulesQuery.error,

    // Selectors
    getRelatedSessions,
    getRelatedRecordings,
    getWorksheetsForModule,

    // Actions
    refetch: modulesQuery.refetch,
  };
};

// Export query keys for external use
export { modulesKeys };
